import { Dispatch } from "react";
import { Action, Reducer } from "redux";
import StatusCode from "../util/StatusCode";
import { CoreGoogleDevice } from "../models/CoreDeviceModels";
import { RequestState } from "../models/RequestState";
import InstanceClient from "../services/InstanceClient";
import { PageResult } from "../models/CoreModels";
import { DeviceSearchPageRequest } from "../models/Requests";
import { CoreTracking } from "../models/CoreTracking";
import store from './store';
export enum DeviceType {
    LIST_GOOGLE_DEVICES = 'LIST_GOOGLE_DEVICES',
    GET_GOOGLE_DEVICE = 'GET_GOOGLE_DEVICE', 
    GET_GOOGLE_DEVICE_TRACKINGS = 'GET_GOOGLE_DEVICE_TRACKINGS'
}

export interface DeviceState {
    googleDevices: RequestState<PageResult<CoreGoogleDevice>>
    googleDevice: RequestState<CoreGoogleDevice>
    googleDeviceTrackings: RequestState<CoreTracking[]>
}

interface ListGoogleDevicesAction extends Action<DeviceType> {
    type: DeviceType.LIST_GOOGLE_DEVICES,
    googleDevices: RequestState<PageResult<CoreGoogleDevice>>
}

interface GetGoogleDevicesAction extends Action<DeviceType> {
    type: DeviceType.GET_GOOGLE_DEVICE,
    googleDevice: RequestState<CoreGoogleDevice>
}

interface GetGoogleDeviceTrackingsAction extends Action<DeviceType> {
    type: DeviceType.GET_GOOGLE_DEVICE_TRACKINGS,
    googleDeviceTrackings: RequestState<CoreTracking[]>
}
type DeviceAction =
    ListGoogleDevicesAction
    | GetGoogleDevicesAction
    | GetGoogleDeviceTrackingsAction;

const createGoogleDeviceQueryParams = (filter: Partial<DeviceSearchPageRequest>): URLSearchParams => {
    const params = new URLSearchParams();

    for (const key in filter) {
        const value = filter[key as keyof DeviceSearchPageRequest];

        if (value === undefined || value === null || value === '') {
            continue;
        }

        if (Array.isArray(value)) {
            value.forEach(x => params.append(key, x.toString()));
            continue;
        }

        params.set(key, value.toString());
    }

    return params;
}

export const ListGoogleDevices = (filter: Partial<DeviceSearchPageRequest> = {}) => (dispatch: Dispatch<ListGoogleDevicesAction>) => {
    const queryParams = createGoogleDeviceQueryParams(filter);
    const url = '/api/core/googledevice';

    dispatch({
        type: DeviceType.LIST_GOOGLE_DEVICES,
        googleDevices: { code: StatusCode.PENDING }
    })
    return InstanceClient.get<PageResult<CoreGoogleDevice>>(url, queryParams)
        .then(res =>
            dispatch({
                type: DeviceType.LIST_GOOGLE_DEVICES,
                googleDevices: { code: StatusCode.COMPLETE, data: res.data }
            }))
        .catch(error =>
            dispatch({
                type: DeviceType.LIST_GOOGLE_DEVICES,
                googleDevices: { code: StatusCode.ERROR, error }
            }))
}

export const GetGoogleDevice = (googleDeviceId:string) => (dispatch: Dispatch<GetGoogleDevicesAction>) => {
    dispatch({
        type: DeviceType.GET_GOOGLE_DEVICE, 
        googleDevice: {code:StatusCode.PENDING }
    })
    return InstanceClient.get<CoreGoogleDevice>(`/api/core/googledevice/${googleDeviceId}`)
        .then(res => {
            dispatch({
                type: DeviceType.GET_GOOGLE_DEVICE,
                googleDevice: {code:StatusCode.COMPLETE, data: res.data }
            })
        })
        .catch (error => {
            dispatch({
                type: DeviceType.GET_GOOGLE_DEVICE,
                googleDevice: {code:StatusCode.ERROR, error}
            })
        })
}

export const GetGoogleDeviceTrackings = (googleDeviceId: string, update: boolean = false) => (dispatch: Dispatch<GetGoogleDeviceTrackingsAction>) => {
    const storeKey = store.getState().device.googleDeviceTrackings?.key;
    if (googleDeviceId === storeKey && !update) {
        return;
    }
    dispatch({
        type: DeviceType.GET_GOOGLE_DEVICE_TRACKINGS,
        googleDeviceTrackings: { code: StatusCode.PENDING }
    })
    return InstanceClient.get<CoreTracking[]>(`/api/core/googledevice/${googleDeviceId}/trackings`)
        .then(res => {
            dispatch({
                type: DeviceType.GET_GOOGLE_DEVICE_TRACKINGS,
                googleDeviceTrackings: { code: StatusCode.COMPLETE, data: res.data }
            })
        })
        .catch(error => {
            dispatch({
                type: DeviceType.GET_GOOGLE_DEVICE_TRACKINGS,
                googleDeviceTrackings: { code: StatusCode.ERROR, error }
            })
        })
}

const initialState: DeviceState = {
    googleDevices: { code: StatusCode.NONE },
    googleDevice: { code: StatusCode.NONE }, 
    googleDeviceTrackings: {code:StatusCode.NONE}
}

export const reducer: Reducer<DeviceState, DeviceAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case DeviceType.LIST_GOOGLE_DEVICES:
            return {
                ...state,
                googleDevices: action.googleDevices
            };
        case DeviceType.GET_GOOGLE_DEVICE:
            return {
                ...state,
                googleDevice: action.googleDevice
            };
        case DeviceType.GET_GOOGLE_DEVICE_TRACKINGS:
            return {
                ...state,
                googleDeviceTrackings : action.googleDeviceTrackings
            }
        default:
            return state;
    }
}
